
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getLoggedInUser } from '../../utils/authHelper';
import {
    IoArrowBack, IoCalendarOutline, IoPersonOutline, IoChatbubblesOutline, IoReorderFourOutline,
    IoPricetagOutline, IoClose, IoBuildOutline, IoMegaphoneOutline, IoPencil, IoHardwareChipOutline,
    IoNotificationsOutline, IoChevronDown, IoSearch, IoHourglassOutline, IoAttach,
    IoDocumentTextOutline, IoTrash, IoCloudUploadOutline, IoMailOutline, IoSaveOutline,
} from 'react-icons/io5';
import './TaskDetailPage.css';

// --- Configuration from Environment Variables ---
const SWAGGER_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const NODE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;
const AUTH_TOKEN_KEY = 'zTaskAuthToken';

// --- API Helpers ---
const getApiHeaders = (isFormData = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}` };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
        headers['Accept'] = 'application/json';
    }
    return headers;
};
const getTaskById = async (taskId) => { const response = await fetch(`${SWAGGER_BASE_URL}/api/get-task-by-id?task_ID=${taskId}`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch task details'); return response.json(); };
const updateTask = async (taskId, taskData) => { const response = await fetch(`${SWAGGER_BASE_URL}/api/update-task?task_id=${taskId}`, { method: 'PATCH', headers: getApiHeaders(), body: JSON.stringify(taskData) }); if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.message || 'Task update failed'); } return response.json(); };
const getTaskHistory = async () => { const response = await fetch(`${SWAGGER_BASE_URL}/api/get-task-history`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch task history'); const data = await response.json(); return data.message || []; };
const getUsers = async () => { const response = await fetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch users'); const data = await response.json(); return data.userData || []; };
const getProjects = async () => { const response = await fetch(`${SWAGGER_BASE_URL}/api/get-project`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch projects'); const data = await response.json(); return data.projects || []; };
const getFullHierarchyTree = async () => { const response = await fetch(`${SWAGGER_BASE_URL}/api-get-full-hierarchy-tree`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch hierarchy tree'); return response.json(); };
const getAttachments = async (id, moduleName) => { const response = await fetch(`${NODE_BASE_URL}/attachment/api/attachments/${id}/${moduleName}`, { headers: getApiHeaders() }); if (!response.ok) throw new Error('Failed to fetch attachments'); return response.json(); };
const deleteAttachment = async (id) => {
    const response = await fetch(`${NODE_BASE_URL}/attachment/api/attachments/${id}`, { method: 'DELETE', headers: getApiHeaders() });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete attachment');
    }
    return { success: true, message: 'Attachment soft deleted successfully' };
};
const uploadAttachment = async (taskId, moduleName, file) => { const formData = new FormData(); formData.append('files', file); const response = await fetch(`${NODE_BASE_URL}/attachment/api/attachments/${taskId}/${moduleName}`, { method: 'POST', headers: getApiHeaders(true), body: formData }); if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.message || 'File upload failed'); } return response.json(); };

// --- CORRECTED EMAIL FUNCTION (Multipart/Form-Data) ---
const sendTaskEmailWithAttachment = async (recipients, ccRecipients, bccRecipients, taskId, files) => {
    const toParams = (recipients || []).map(email => `to=${encodeURIComponent(email)}`).join('&');
    const ccParams = (ccRecipients || []).map(email => `cc=${encodeURIComponent(email)}`).join('&');
    const bccParams = (bccRecipients || []).map(email => `bcc=${encodeURIComponent(email)}`).join('&');
    const allParams = [toParams, ccParams, bccParams, `taskId=${taskId}`].filter(Boolean).join('&');
    const endpoint = `${SWAGGER_BASE_URL}/api/send-mail-with-attachment?${allParams}`;

    const formData = new FormData();
    // The API expects each file under the key 'attachment'
    files.forEach(file => {
        formData.append('attachment', file);
    });

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: getApiHeaders(true), // Use headers for FormData
        body: formData,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `Request failed with status ${response.status}` }));
        throw new Error(errorData.message || 'Failed to send email');
    }
    
    return response.json().catch(() => ({ success: true, message: 'Email sent successfully.' }));
};

const getColumnData = async (table, column) => { const response = await fetch(`${NODE_BASE_URL}/api/get-column/${table}/${column}`, { headers: getApiHeaders() }); if (!response.ok) throw new Error(`Failed to fetch ${column}`); return response.json(); };
const getStatuses = () => getColumnData('common_master', 'current_status');
const getPriorities = () => getColumnData('common_master', 'priority_in_daily_task');
const getCategories = () => getColumnData('common_master', 'category');
const getCustomers = () => fetch(`${NODE_BASE_URL}/customer/api/customers/`, { headers: getApiHeaders() }).then(res => res.ok ? res.json() : Promise.reject('Failed to fetch customers'));

// --- Constants & Utilities ---
const MESSAGE_TIMEOUT_DURATION = 5000;
const padZero = (num) => String(num).padStart(2, '0');

// --- FIX STARTS HERE ---

// 1. Parse date strings from the API as UTC
const parseDateString = (dateString) => {
    if (!dateString) return null;
    // Append 'Z' to ISO-like strings that lack a timezone, forcing them to be parsed as UTC.
    const correctedDateString = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(dateString)
        ? `${dateString}Z`
        : dateString;
    const date = new Date(correctedDateString);
    return !isNaN(date.getTime()) ? date : null;
};

// 2. Display the date using UTC methods to prevent local timezone conversion
const formatDateForDisplay = (dateString, includeTime = false) => {
    const date = parseDateString(dateString); // This now correctly parses the string as UTC
    if (!date) return 'N/A';
    try {
        // USE UTC GETTERS to read the date/time components. This ignores the browser's local timezone.
        const day = padZero(date.getUTCDate());
        const month = padZero(date.getUTCMonth() + 1); // getUTCMonth is 0-indexed
        const year = date.getUTCFullYear();

        if (includeTime) {
            let hours = date.getUTCHours();
            const minutes = padZero(date.getUTCMinutes());
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM/PM
            const timeString = `${padZero(hours)}:${minutes} ${ampm}`;
            return `${day}/${month}/${year}, ${timeString}`;
        }
        return `${day}/${month}/${year}`;
    } catch (e) {
        return 'N/A';
    }
};

// --- FIX ENDS HERE ---

const formatDateTimeForReport = () => { const now = new Date(); const day = now.getDate(); const month = now.getMonth() + 1; const year = now.getFullYear(); let hours = now.getHours(); const minutes = now.getMinutes(); const seconds = now.getSeconds(); const ampm = hours >= 12 ? 'PM' : 'AM'; hours = hours % 12; hours = hours ? hours : 12; return `${day}/${month}/${year}, ${hours}:${padZero(minutes)}:${padZero(seconds)} ${ampm}`; };
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// --- Hierarchy Helpers ---
const findNodeForUser = (tree, userName) => {
    for (const node of tree) {
        if (node.name === userName) {
            return node;
        }
        if (node.children) {
            const found = findNodeForUser(node.children, userName);
            if (found) return found;
        }
    }
    return null;
};
const getAllSubordinates = (node) => {
    let subordinates = [];
    if (!node || !node.children) {
        return [];
    }
    for (const child of node.children) {
        subordinates.push(child);
        subordinates = subordinates.concat(getAllSubordinates(child));
    }
    return subordinates;
};
const findManagerForUser = (tree, userName) => { for (const managerNode of tree) { if (managerNode.children && managerNode.children.length > 0) { const isDirectReport = managerNode.children.some(child => child.name === userName); if (isDirectReport) return managerNode; const foundManager = findManagerForUser(managerNode.children, userName); if (foundManager) return foundManager; } } return null; };


// --- Date/Time helpers ---
const allMonths = [ { value: '1', name: 'Jan' }, { value: '2', name: 'Feb' }, { value: '3', name: 'Mar' }, { value: '4', name: 'Apr' }, { value: '5', name: 'May' }, { value: '6', name: 'Jun' }, { value: '7', name: 'Jul' }, { value: '8', name: 'Aug' }, { value: '9', name: 'Sep' }, { value: '10', name: 'Oct' }, { value: '11', name: 'Nov' }, { value: '12', name: 'Dec' } ];
const allHours = Array.from({ length: 24 }, (_, i) => padZero(i));
const allMinutes = Array.from({ length: 60 }, (_, i) => padZero(i));

// --- PDF Generation Function ---
const generateTaskSummaryPdf = (taskData, existingAttachments, allAssets = []) => {
    const doc = new jsPDF();
    const {
        title, taskId, status, priority, description, createdBy, assigneeName,
        createdAt, updatedAt, dueDate, projectName, customer, category, assetName
    } = taskData;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(33, 33, 33);
    doc.text(`Task Details: ${title} (#${taskId})`, 14, 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Report Generated: ${formatDateTimeForReport()}`, 14, 28);
    const mainData = [
        ['Status', status || 'N/A'],
        ['Priority', priority || 'N/A'],
        ['Assignee', assigneeName || 'N/A'],
        ['Category', (typeof category === 'object' ? category.name : category) || 'N/A'],
        ['Project', (typeof taskData.project === 'object' ? taskData.project.projectName : projectName) || 'N/A'],
        ['Customer', customer?.name || 'N/A'],
        ['Created On', formatDateForDisplay(createdAt, true)],
        ['Last Updated', formatDateForDisplay(updatedAt, true)],
        ['Due Date', formatDateForDisplay(dueDate, true)],
    ];
    autoTable(doc, {
        startY: 38,
        head: [['Field', 'Value']],
        body: mainData,
        theme: 'grid',
        headStyles: { fillColor: '#16a085', textColor: 255, fontStyle: 'bold' },
        styles: { font: 'helvetica', fontSize: 10, cellPadding: 2.5 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: { 0: { fontStyle: 'bold' } },
    });
    let lastY = doc.lastAutoTable.finalY;

    const taskAssetNames = assetName || [];
    const assetsForPdf = allAssets.filter(asset => taskAssetNames.includes(asset.name));

    if (assetsForPdf.length > 0) {
        lastY += 10;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 33, 33);
        doc.text('Associated Assets', 14, lastY);
        lastY += 3;
        
        autoTable(doc, {
            startY: lastY,
            head: [['Asset Name', 'Asset Code', 'Registration #', 'Serial #']],
            body: assetsForPdf.map(a => [a.name, a.code, a.reg, a.serial]),
            theme: 'grid',
            headStyles: { fillColor: '#16a085', textColor: 255, fontStyle: 'bold' },
            styles: { font: 'helvetica', fontSize: 10, cellPadding: 2.5 },
            alternateRowStyles: { fillColor: [245, 245, 245] },
        });
        lastY = doc.lastAutoTable.finalY;
    }

    lastY += 15;
    if (description) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(33, 33, 33);
        doc.text('Description', 14, lastY);
        lastY += 8;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        const descriptionLines = doc.splitTextToSize(description, 180);
        doc.text(descriptionLines, 14, lastY);
        lastY += (descriptionLines.length * 5) + 10;
    }
    if (existingAttachments && existingAttachments.length > 0) {
        autoTable(doc, {
            startY: lastY,
            head: [['Attached Files']],
            body: existingAttachments.map(att => [decodeURIComponent(att.file_url.split('/').pop())]),
            theme: 'grid',
            headStyles: { fillColor: '#2c3e50', textColor: 255, fontStyle: 'bold' },
        });
    }
    const pdfBlob = doc.output('blob');
    return new File([pdfBlob], `Task-Summary-${taskId}.pdf`, { type: 'application/pdf' });
};

// --- Helper to convert a URL to a File object ---
const urlToFile = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const blob = await response.blob();
        const fileName = decodeURIComponent(url.split('/').pop());
        return new File([blob], fileName, { type: blob.type });
    } catch (error) {
        console.error(`Could not convert URL to file: ${url}`, error);
        return null; // Return null if fetching fails
    }
};


// --- Reusable Sub-Components ---
const SearchableDropdown = ({
    options = [],
    selectedValues,
    onChange,
    placeholder,
    isMultiSelect = false,
    labelField = 'displayName',
    valueField = 'id',
    disabled = false,
    error = false,
    allowClear = false,
    forcePlaceholder = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuStyle, setMenuStyle] = useState({});
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const safeOptions = Array.isArray(options) ? options : [];
    const filteredOptions = safeOptions.filter((option) =>
        option && option[labelField] && typeof option[labelField] === 'string'
            ? option[labelField].toLowerCase().includes(searchTerm.toLowerCase())
            : false
    );

    const handleSelect = (optionValue) => {
        if (isMultiSelect) {
            const current = Array.isArray(selectedValues) ? selectedValues : [];
            const newValues = current.includes(optionValue)
                ? current.filter((v) => v !== optionValue)
                : [...current, optionValue];
            onChange(newValues);
        } else {
            onChange(optionValue);
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const getDisplayValue = () => {
        if (forcePlaceholder) return placeholder;
        if (isMultiSelect) {
            if (!selectedValues || selectedValues.length === 0) return placeholder;
            if (selectedValues.length === 1) {
                const selectedOption = safeOptions.find((opt) => String(opt[valueField]) === String(selectedValues[0]));
                return selectedOption ? (selectedOption.name || selectedOption[labelField]) : '1 selected';
            }
            return `${selectedValues.length} items selected`;
        }
        const selectedOption = safeOptions.find((opt) => String(opt[valueField]) === String(selectedValues));
        return selectedOption ? selectedOption[labelField] : placeholder;
    };

    const updateMenuPosition = useCallback(() => {
        if (!dropdownRef.current) return;
        const rect = dropdownRef.current.getBoundingClientRect();
        setMenuStyle({
            position: 'absolute',
            top: rect.bottom + window.scrollY + 4,
            left: rect.left + window.scrollX,
            width: Math.max(rect.width, 220),
            zIndex: 10050,
        });
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        updateMenuPosition();
        const onReposition = () => updateMenuPosition();
        window.addEventListener('scroll', onReposition, true);
        window.addEventListener('resize', onReposition);
        return () => {
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition);
        };
    }, [isOpen, updateMenuPosition, filteredOptions.length]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (event) => {
            const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
            const inMenu = menuRef.current && menuRef.current.contains(event.target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div className={`searchable-dropdown-container${isOpen ? ' is-open' : ''}`} ref={dropdownRef}>
            <button
                type="button"
                className={`dropdown-trigger ${error ? 'invalid-field' : ''}`}
                onClick={() => !disabled && setIsOpen((open) => !open)}
                disabled={disabled}
            >
                <span>{getDisplayValue()}</span>
                <IoChevronDown className={`dropdown-chevron ${isOpen ? 'open' : ''}`} />
            </button>
            {isOpen &&
                createPortal(
                    <div className="dropdown-panel ui-portal-dropdown-menu" style={menuStyle} ref={menuRef}>
                        <div className="dropdown-search-container ui-portal-dropdown-search">
                            <input
                                type="text"
                                className="dropdown-search-input"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                            <IoSearch className="search-icon" aria-hidden="true" />
                        </div>
                        <ul className="dropdown-options-list">
                            {!isMultiSelect && allowClear && (
                                <li className="dropdown-option clear-option" onClick={() => handleSelect('')}>
                                    — Clear Selection —
                                </li>
                            )}
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option) => {
                                    const isSelected = isMultiSelect
                                        ? (Array.isArray(selectedValues) ? selectedValues : []).includes(option[valueField])
                                        : String(selectedValues) === String(option[valueField]);
                                    return (
                                        <li
                                            key={String(option[valueField])}
                                            className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleSelect(option[valueField])}
                                        >
                                            <div className="option-content-wrapper">
                                                {isMultiSelect && (
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        readOnly
                                                        className="dropdown-checkbox"
                                                    />
                                                )}
                                                <div className="option-label">
                                                    <span className="option-label-name">
                                                        {option.name || option[labelField]}
                                                    </span>
                                                    {option.details && (
                                                        <span className="option-label-details">{option.details}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })
                            ) : (
                                <li className="dropdown-option-message">No results found</li>
                            )}
                        </ul>
                    </div>,
                    document.body
                )}
        </div>
    );
};
const CommentItem = ({ commentText, timestamp }) => { if (!commentText || commentText.includes('| reason :')) return null; let author = 'Unknown'; let message = commentText.trim(); const firstColonIndex = commentText.indexOf(':'); if (firstColonIndex > -1) { author = commentText.substring(0, firstColonIndex).trim(); message = commentText.substring(firstColonIndex + 1).trim(); const unwantedSuffixRegex = /: Comment\s*$/; if (unwantedSuffixRegex.test(message)) { message = message.replace(unwantedSuffixRegex, '').trim(); } } else { const byMatch = commentText.match(/(.+) by (.+)/); if (byMatch) { const [, text, byAuthor] = byMatch; message = text.trim(); author = byAuthor.trim(); } } if (!message) return null; return (<div className="comment-item"><IoPersonOutline className="comment-icon" /><div className="comment-content"><p className="comment-body"><strong>{author}</strong>: {message}</p></div><span className="comment-date">{formatDateForDisplay(timestamp, true)}</span></div>); };
const RemarkItem = ({ remarkText, timestamp }) => { if (!remarkText) return null; const match = remarkText.match(/(.+) by (.+)/); const action = match ? match[1] : remarkText; const author = match ? match[2] : 'System'; return (<div className="remark-item"><IoMegaphoneOutline className="remark-icon" /><div className="remark-content"><p className="remark-body"><strong>{author}</strong> reported: "{action.trim()}"</p></div><span className="remark-date">{formatDateForDisplay(timestamp, true)}</span></div>); };
const SystemLogItem = ({ logText, timestamp }) => { if (!logText || !logText.includes('| reason :')) { return null; } const systemMatch = logText.match(/(.+) by (.+) \| reason : (.+)/); if (systemMatch) { const [, action, author, reason] = systemMatch; return (<div className="remark-item system-log"><IoBuildOutline className="remark-icon" /><div className="remark-content"><p className="remark-body"><strong>{author}</strong> {action.toLowerCase().replace("has been", "")}.</p><p className="comment-reason">Reason: {reason}</p></div><span className="remark-date">{formatDateForDisplay(timestamp, true)}</span></div>); } return null; };
const AttachmentItem = ({ attachment, onDelete, canDelete }) => { const fileName = decodeURIComponent(attachment.file_url.split('/').pop()); const uploaderName = attachment.added_by || 'Unknown'; return (<div className="attachment-item"><a href={attachment.file_url} download={fileName} target="_blank" rel="noopener noreferrer" className="attachment-download-link" title={`Download ${fileName}`}><IoDocumentTextOutline className="attachment-icon" /><div className="attachment-details"><span className="attachment-filename">{fileName}</span><span className="attachment-uploader">Added by: {uploaderName}</span></div></a>{canDelete && <button onClick={() => onDelete(attachment.id)} className="attachment-delete-btn" title="Delete attachment"><IoTrash /></button>}</div>); };

// --- Main Page Component ---
const TaskDetailPage = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [isLoadingTask, setIsLoadingTask] = useState(true);
    const [taskHistory, setTaskHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [attachments, setAttachments] = useState([]);
    const [isLoadingAttachments, setIsLoadingAttachments] = useState(true);
    const [activeTab, setActiveTab] = useState('details');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isEmailScreenOpen, setIsEmailScreenOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isAssigneesManager, setIsAssigneesManager] = useState(false);
    const [hasUpdated, setHasUpdated] = useState(false);
    const [availableStatuses, setAvailableStatuses] = useState([]);
    const [availablePriorities, setAvailablePriorities] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableAssignees, setAvailableAssignees] = useState([]);
    const [visibleAssignees, setVisibleAssignees] = useState([]);
    const [availableRecipients, setAvailableRecipients] = useState([]);
    const [availableAssets, setAvailableAssets] = useState([]);
    const [availableProjects, setAvailableProjects] = useState([]);
    const [availableCustomers, setAvailableCustomers] = useState([]);
    const [remainingTime, setRemainingTime] = useState(null);
    const [emailFormData, setEmailFormData] = useState({ recipients: [], ccRecipients: [], bccRecipients: [], selectedAttachmentIds: [] });
    const [manualEmail, setManualEmail] = useState({ to: '', cc: '', bcc: '' });
    const [emailError, setEmailError] = useState({ to: '', cc: '', bcc: '', general: '' });
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [hierarchy, setHierarchy] = useState([]);
    const [canCompleteTask, setCanCompleteTask] = useState(false);
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const [editFormData, setEditFormData] = useState({});
    const [editFormErrors, setEditFormErrors] = useState({});
    const [isDueDateDirty, setIsDueDateDirty] = useState(false);
    const [showDueDateReason, setShowDueDateReason] = useState(false);
    const [dueDateChangeReason, setDueDateChangeReason] = useState('');
    const [dueDateReasonError, setDueDateReasonError] = useState('');

    // **FIX:** Moved these permission-based constants before they are used in useCallback hooks.
    const isTaskCompleted = (task?.status || '').toLowerCase() === 'completed';
    const canPerformActionsAfterCompletion = isSuperAdmin || isAssigneesManager;
    const canEditTask = !isTaskCompleted || canPerformActionsAfterCompletion;
    const canUploadAttachment = !isTaskCompleted || canPerformActionsAfterCompletion;

    const fetchAllData = useCallback(async () => {
        if (!taskId) return;
        setIsLoadingTask(true); setIsLoadingHistory(true); setIsLoadingAttachments(true);
        try {
            const numericTaskId = parseInt(taskId, 10);
            const [taskResponse, historyResponse, attachmentsResponse] = await Promise.all([getTaskById(taskId), getTaskHistory(), getAttachments(taskId, 'attachment_master')]);
            if (taskResponse && taskResponse.task) {
                setTask(taskResponse.task);
                setRemainingTime(taskResponse.task.remainingTime || 'N/A');
            } else {
                setTask(null); throw new Error("Task data could not be loaded.");
            }
            const allHistoryForThisTask = (historyResponse || []).filter(h => h.parentTaskId === numericTaskId).sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
            setTaskHistory(allHistoryForThisTask);
            setAttachments((attachmentsResponse || []).filter(attachment => attachment.active));
        } catch (error) {
            console.error("Failed to fetch page data:", error);
            setErrorMessage("Could not load task details. " + error.message);
        } finally {
            setIsLoadingTask(false); setIsLoadingHistory(false); setIsLoadingAttachments(false);
        }
    }, [taskId]);

    useEffect(() => {
        const user = getLoggedInUser(); setLoggedInUser(user); setIsSuperAdmin(user?.role?.name?.toUpperCase() === 'SUPER_ADMIN');
        const fetchInitialDropdownData = async () => {
            const results = await Promise.allSettled([getStatuses(), getPriorities(), getCategories(), getUsers(), getProjects(), getCustomers(), getFullHierarchyTree(), getColumnData('assets', 'name'), getColumnData('assets', 'registration_number'), getColumnData('assets', 'serial_number'), getColumnData('assets', 'asset_code')]);
            const [statusesRes, prioritiesRes, categoriesRes, usersRes, projectsRes, customersRes, hierarchyRes, assetNamesRes, assetRegNumbersRes, assetSerialNumbersRes, assetCodesRes] = results;
            if (statusesRes.status === 'fulfilled') setAvailableStatuses(statusesRes.value?.map(s => s.value) || []);
            if (prioritiesRes.status === 'fulfilled') setAvailablePriorities(prioritiesRes.value?.map(p => p.value) || []);
            if (categoriesRes.status === 'fulfilled') setAvailableCategories(categoriesRes.value?.map(c => c.value) || []);
            
            if (usersRes.status === 'fulfilled' && usersRes.value) { 
                setAvailableAssignees(usersRes.value); 
                const recipients = (usersRes.value || []).filter(u => u.email).map(u => ({ 
                    id: u.email, 
                    name: u.name, 
                    details: `(${u.email})`,
                    displayName: `${u.name} (${u.email})`
                })); 
                setAvailableRecipients(recipients); 
            }

            if (projectsRes.status === 'fulfilled' && projectsRes.value) { setAvailableProjects((projectsRes.value || []).map(p => ({ id: String(p.projectId), name: p.projectName }))); }
            if (customersRes.status === 'fulfilled') { setAvailableCustomers((customersRes.value || []).filter(c => c.active).map(c => ({ id: String(c.id), name: c.name }))); }
            if (hierarchyRes.status === 'fulfilled') setHierarchy(hierarchyRes.value?.tree || []);
            if (assetNamesRes.status === 'fulfilled' && assetRegNumbersRes.status === 'fulfilled' && assetSerialNumbersRes.status === 'fulfilled' && assetCodesRes.status === 'fulfilled') {
                const mergedAssets = (assetNamesRes.value || []).map(nameAsset => {
                    const regAsset = (assetRegNumbersRes.value || []).find(r => r.id === nameAsset.id);
                    const serialAsset = (assetSerialNumbersRes.value || []).find(s => s.id === nameAsset.id);
                    const codeAsset = (assetCodesRes.value || []).find(c => c.id === nameAsset.id);
                    
                    const code = codeAsset?.value || 'N/A';
                    const reg = regAsset?.value || 'N/A';
                    const serial = serialAsset?.value || 'N/A';
                    
                    const details = `| Code: ${code} | Reg: ${reg} | S/N: ${serial}`;
                    
                    return { 
                        id: nameAsset.id, 
                        name: nameAsset.value, 
                        details: details, 
                        displayName: `${nameAsset.value} ${details}`,
                        code,
                        reg,
                        serial,
                    };
                });
                setAvailableAssets(mergedAssets);
            }
        };
        fetchInitialDropdownData(); fetchAllData();
    }, [taskId, fetchAllData]);
    
    useEffect(() => {
        if (!loggedInUser || !hierarchy.length || !availableAssignees.length) {
            return;
        }

        if (isSuperAdmin) {
            setVisibleAssignees(availableAssignees);
            return;
        }

        const userNode = findNodeForUser(hierarchy, loggedInUser.name);

        let subordinateList = [];
        if (userNode) {
            subordinateList = getAllSubordinates(userNode);
        }

        const subordinateNames = subordinateList.map(sub => sub.name);
        const subordinateUsers = availableAssignees.filter(user => subordinateNames.includes(user.name));
        const selfUser = availableAssignees.find(user => user.id === loggedInUser.id);
        const finalAssignableUsers = new Map();
        
        if (selfUser) {
            finalAssignableUsers.set(selfUser.id, selfUser);
        }

        subordinateUsers.forEach(user => {
            if (!finalAssignableUsers.has(user.id)) {
                finalAssignableUsers.set(user.id, user);
            }
        });

        setVisibleAssignees(Array.from(finalAssignableUsers.values()));

    }, [loggedInUser, hierarchy, availableAssignees, isSuperAdmin]);

    useEffect(() => { if (!loggedInUser || !task) { setCanCompleteTask(false); return; } if (isSuperAdmin) { setCanCompleteTask(true); return; } if (loggedInUser.name === task.assigneeName) { setCanCompleteTask(true); return; } if (hierarchy.length) { const assigneeManager = findManagerForUser(hierarchy, task.assigneeName); if (assigneeManager && assigneeManager.name === loggedInUser.name) { setCanCompleteTask(true); return; } } setCanCompleteTask(false); }, [loggedInUser, task, hierarchy, isSuperAdmin]);
    useEffect(() => { if (hierarchy.length && loggedInUser && task) { const manager = findManagerForUser(hierarchy, task.assigneeName); setIsAssigneesManager(manager && manager.name === loggedInUser.name); } else { setIsAssigneesManager(false); } }, [hierarchy, loggedInUser, task]);

    const handleGoBack = () => {
        navigate('/tasks', {
            state: {
                refresh: hasUpdated,
                initialFilter: {
                    taskId: taskId,
                },
            },
        });
    };

    const uploadFiles = useCallback(async (filesToUpload) => {
        if (!filesToUpload || filesToUpload.length === 0) return;
        setIsUploading(true);
        setErrorMessage('');
        setSuccessMessage('');
        
        const uploadPromises = filesToUpload.map(file => 
            uploadAttachment(taskId, 'attachment_master', file)
        );
        
        const results = await Promise.allSettled(uploadPromises);
        
        const successfulUploads = results.filter(r => r.status === 'fulfilled');
        const failedUploads = results.filter(r => r.status === 'rejected');

        if (successfulUploads.length > 0) {
            setSuccessMessage(`${successfulUploads.length} file(s) uploaded successfully.`);
            await fetchAllData(); // Refresh attachments list
        }
        if (failedUploads.length > 0) {
            const failedFileNames = filesToUpload
                .filter((_, index) => results[index].status === 'rejected')
                .map(file => file.name)
                .join(', ');
            setErrorMessage(`Failed to upload: ${failedFileNames}. Please try again.`);
        }
        
        setIsUploading(false);
    }, [taskId, fetchAllData]);

    const handleFileChange = (event) => {
        if (event.target.files) {
            uploadFiles(Array.from(event.target.files));
        }
        event.target.value = null;
    };

    const handleFileDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (isUploading || !canUploadAttachment) return;
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            uploadFiles(Array.from(e.dataTransfer.files));
            e.dataTransfer.clearData();
        }
    }, [isUploading, canUploadAttachment, uploadFiles]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isUploading || !canUploadAttachment) return;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    }, [isUploading, canUploadAttachment]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);
    
    const handleDeleteAttachment = async (attachmentId) => {
        if (window.confirm('Are you sure you want to delete this file?')) {
            setErrorMessage('');
            setSuccessMessage('');
            try {
                await deleteAttachment(attachmentId);
                setSuccessMessage('Attachment deleted successfully.');
                await fetchAllData();
            } catch (error) {
                console.error("Failed to delete attachment:", error);
                setErrorMessage(error.message || 'Failed to delete attachment.');
            }
        }
    };

    const handleOpenEmailScreen = () => {
        const assignee = availableAssignees.find(u => u.name === task.assigneeName);
        setEmailFormData({ 
            recipients: assignee ? [assignee.email] : [], 
            ccRecipients: [], 
            bccRecipients: [], 
            selectedAttachmentIds: [] 
        });
        setManualEmail({ to: '', cc: '', bcc: '' });
        setEmailError({ to: '', cc: '', bcc: '', general: '' });
        setSuccessMessage('');
        setErrorMessage('');
        setIsEmailScreenOpen(true);
    };

    const handleCloseEmailScreen = () => setIsEmailScreenOpen(false);

    const handleAddRecipient = (type) => {
        const email = manualEmail[type].trim();
        if (!email) return;

        if (!isValidEmail(email)) {
            setEmailError(prev => ({ ...prev, [type]: "Invalid email format." }));
            return;
        }
        
        const allRecipients = [...emailFormData.recipients, ...emailFormData.ccRecipients, ...emailFormData.bccRecipients];
        if (allRecipients.includes(email)) {
            setEmailError(prev => ({ ...prev, [type]: "Email already added." }));
            return;
        }
        
        const key = type === 'to' ? 'recipients' : `${type}Recipients`;
        setEmailFormData(prev => ({ ...prev, [key]: [...prev[key], email] }));
        setManualEmail(prev => ({ ...prev, [type]: '' }));
        setEmailError(prev => ({ ...prev, [type]: '' }));
    };

    const handleRemoveRecipient = (type, email) => {
        const key = type === 'to' ? 'recipients' : `${type}Recipients`;
        setEmailFormData(prev => ({ ...prev, [key]: prev[key].filter(r => r !== email) }));
    };

    const handleSendEmail = async () => {
        if (emailFormData.recipients.length === 0) {
            setEmailError(prev => ({ ...prev, general: "Please add at least one 'To' recipient." }));
            return;
        }
        
        setIsGeneratingPdf(true);
        setEmailError({ to: '', cc: '', bcc: '', general: '' });
        setSuccessMessage('Fetching latest task data...');

        try {
            const latestTaskResponse = await getTaskById(taskId);
            const latestTask = latestTaskResponse.task;
            if (!latestTask) throw new Error("Could not get latest task details.");

            setSuccessMessage('Generating task summary PDF...');
            const summaryPdfFile = generateTaskSummaryPdf(latestTask, attachments, availableAssets);

            setSuccessMessage('Preparing selected attachments...');
            const selectedAttachments = attachments.filter(att => emailFormData.selectedAttachmentIds.includes(att.id));
            
            const filePromises = selectedAttachments.map(att => urlToFile(att.file_url));
            const selectedFiles = (await Promise.all(filePromises)).filter(Boolean);

            const allFilesToSend = [summaryPdfFile, ...selectedFiles];

            setSuccessMessage('Sending email...');
            setIsSendingEmail(true);

            await sendTaskEmailWithAttachment(
                emailFormData.recipients, 
                emailFormData.ccRecipients, 
                emailFormData.bccRecipients, 
                taskId, 
                allFilesToSend
            );
            
            setSuccessMessage("Email sent successfully! Returning to task details...");
            setTimeout(() => {
                handleCloseEmailScreen();
                setSuccessMessage("Email sent successfully!"); 
                setTimeout(() => setSuccessMessage(''), MESSAGE_TIMEOUT_DURATION);
            }, 2000);

        } catch (err) {
            console.error("Failed to send email:", err);
            setErrorMessage(err.message || "An unknown error occurred.");
        } finally {
            setIsGeneratingPdf(false);
            setIsSendingEmail(false);
        }
    };
    
    const handleRecipientChange = (type, selectedEmailsFromDropdown) => {
        const key = type === 'to' ? 'recipients' : `${type}Recipients`;
        const manuallyAddedEmails = emailFormData[key].filter(email => !availableRecipients.find(rec => rec.id === email));
        const newRecipientList = [...new Set([...selectedEmailsFromDropdown, ...manuallyAddedEmails])];
        setEmailFormData(prev => ({ ...prev, [key]: newRecipientList }));
    };

    const renderRecipientInput = (type) => {
        const typeLabels = { to: 'To:', cc: 'CC (Optional):', bcc: 'BCC (Optional):' };
        const recipientList = type === 'to' ? emailFormData.recipients : emailFormData[`${type}Recipients`];
        const dropdownSelection = recipientList.filter(email => availableRecipients.some(rec => rec.id === email));

        return (
            <div className="form-group">
                <label>{typeLabels[type]}</label>
                <div className="recipient-pills-container">
                    {(recipientList || []).map(email => (
                        <div key={email} className="recipient-pill">
                            <span>{availableRecipients.find(r => r.id === email)?.name || email}</span>
                            <button type="button" onClick={() => handleRemoveRecipient(type, email)}><IoClose /></button>
                        </div>
                    ))}
                </div>
                <SearchableDropdown
                    options={availableRecipients}
                    selectedValues={dropdownSelection}
                    onChange={(values) => handleRecipientChange(type, values)}
                    placeholder="Select existing users..."
                    isMultiSelect={true}
                />
                <div className="manual-email-entry">
                    <input type="email" placeholder="Type external email and press Enter..." value={manualEmail[type]} onChange={(e) => { setManualEmail(p => ({ ...p, [type]: e.target.value })); if (emailError[type]) setEmailError(p => ({ ...p, [type]: '' })); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(type); } }} />
                    <button type="button" onClick={() => handleAddRecipient(type)}>Add</button>
                </div>
                {emailError[type] && <span className="error-text">{emailError[type]}</span>}
            </div>
        );
    };

    const handleEnterEditMode = () => {
        if (!task) return;
        
        setIsDueDateDirty(false);
        setErrorMessage(''); 
        setEditFormErrors({}); 
        setShowDueDateReason(false); 
        setDueDateChangeReason(''); 
        setDueDateReasonError('');

        const currentAssignee = availableAssignees.find(u => u.name === task.assigneeName);
        const projectIdentifier = (task.project && typeof task.project === 'object' ? task.project.projectName : task.project) || task.projectName;
        const currentProject = availableProjects.find(p => p.name === projectIdentifier);
        const currentAssetIds = (task.assetName || []).map(name => { const asset = availableAssets.find(a => a.name === name); return asset ? asset.id : null; }).filter(Boolean);

        const initialEditFormData = {
            category: (typeof task.category === 'object' ? task.category.name : task.category) || '',
            title: task.title || '',
            description: task.description || '',
            status: (task.status || '').toLowerCase().replace(/_/g, ' '),
            priority: task.priority || '',
            assigneeId: currentAssignee ? String(currentAssignee.id) : '',
            projectId: currentProject ? String(currentProject.id) : '',
            customerId: task.customerId ? String(task.customerId) : '',
            dueDay: '',
            dueMonth: '',
            dueYear: '',
            hour: '',
            minute: '',
            assetIds: currentAssetIds,
            newComment: '',
            reminderDays: task.reminderDays || 1,
        };

        if (task.dueDate) {
            const existingDueDate = parseDateString(task.dueDate);
            if (existingDueDate) {
                initialEditFormData.dueDay = String(existingDueDate.getUTCDate());
                const monthObject = allMonths.find(m => parseInt(m.value, 10) === existingDueDate.getUTCMonth() + 1);
                if (monthObject) {
                    initialEditFormData.dueMonth = monthObject.name;
                }
                initialEditFormData.dueYear = String(existingDueDate.getUTCFullYear());
                initialEditFormData.hour = padZero(existingDueDate.getUTCHours());
                initialEditFormData.minute = padZero(existingDueDate.getUTCMinutes());
            }
        }

        setEditFormData(initialEditFormData);
        setIsEditMode(true);
    };

    const handleExitEditMode = () => setIsEditMode(false);

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        if (['dueDay', 'dueMonth', 'dueYear', 'hour', 'minute'].includes(name)) { 
            setIsDueDateDirty(true); 
        }
        setEditFormData(prev => ({ ...prev, [name]: value }));
        if (editFormErrors[name]) { 
            setEditFormErrors(prev => ({ ...prev, [name]: null })); 
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditFormErrors({});
        setErrorMessage('');
        setDueDateReasonError('');

        const newStatus = editFormData.status.toLowerCase();
        if (newStatus === 'completed' && !canCompleteTask) {
            setErrorMessage("You do not have permission to mark this task as 'Completed'.");
            return;
        }

        if (isDueDateDirty) {
            const { dueDay, dueMonth, dueYear, hour, minute } = editFormData;
            const areAllFieldsFilled = dueDay && dueMonth && dueYear && hour && minute;
            const isAnyFieldFilled = dueDay || dueMonth || dueYear || hour || minute;

            if (isAnyFieldFilled && !areAllFieldsFilled) {
                setErrorMessage("To set a new due date, please fill all date and time fields completely.");
                return;
            }

            if (areAllFieldsFilled) {
                const monthObject = allMonths.find(m => m.name === dueMonth);
                if (!monthObject) {
                    setErrorMessage("Invalid month selected.");
                    return;
                }
                const monthIndex = parseInt(monthObject.value, 10) - 1;
                
                const selectedTimestamp = Date.UTC(dueYear, monthIndex, dueDay, hour, minute);
                
                if (isNaN(selectedTimestamp)) {
                    setErrorMessage("The selected date is invalid. Please check the day and month.");
                    return;
                }
                
                // Compare UTC timestamp against current UTC time
                if (selectedTimestamp < Date.now()) {
                    setErrorMessage("The due date cannot be in the past. Please select a future date and time.");
                    return;
                }

                if (!dueDateChangeReason.trim()) {
                    setDueDateReasonError('A reason is required when changing the due date.');
                    return;
                }
            }
        }

        setIsSubmittingEdit(true);
        try {
            let combinedDateTime = task.dueDate;

            if (isDueDateDirty) {
                const { dueYear, dueMonth, dueDay, hour, minute } = editFormData;
                if (dueYear && dueMonth && dueDay && hour && minute) {
                    const monthObject = allMonths.find(m => m.name === dueMonth);
                    const monthIndex = parseInt(monthObject.value, 10) - 1;
                    
                    const date = new Date(Date.UTC(dueYear, monthIndex, dueDay, hour, minute));
                    
                    combinedDateTime = date.toISOString();
                } else {
                    combinedDateTime = null;
                }
            }

            const payload = {
                title: editFormData.title,
                description: editFormData.description,
                status: editFormData.status,
                priority: editFormData.priority,
                category: editFormData.category,
                assigneeName: availableAssignees.find(u => String(u.id) === String(editFormData.assigneeId))?.name || '',
                projectName: availableProjects.find(p => String(p.id) === String(editFormData.projectId))?.name || null,
                customerId: editFormData.customerId ? parseInt(editFormData.customerId, 10) : null,
                assetName: (editFormData.assetIds || []).map(id => availableAssets.find(a => a.id === id)?.name).filter(Boolean),
                dueDate: combinedDateTime,
                reminderDays: Number(editFormData.reminderDays) || 1,
            };

            if (editFormData.newComment) payload.comments = editFormData.newComment.trim();
            if (isDueDateDirty && dueDateChangeReason) payload.comments = payload.comments ? `${payload.comments}\n(Due date change reason:) ${dueDateChangeReason}` : `(Due date change reason:) ${dueDateChangeReason}`;

            await updateTask(taskId, payload);
            handleExitEditMode();
            await fetchAllData();
            setHasUpdated(true);
        } catch (err) {
            setErrorMessage(err.message || 'Failed to update task.');
        } finally {
            setIsSubmittingEdit(false);
        }
    };
    
    const processedComments = React.useMemo(() => {
        if (isLoadingHistory) return [];
        
        const historyOldestFirst = [...taskHistory].sort((a, b) => new Date(a.createdAt || a.updatedAt) - new Date(b.createdAt || b.updatedAt));

        const uniqueComments = new Map();

        historyOldestFirst.forEach(h => {
            const ts = h.updatedAt || h.createdAt;
            const userComments = (h.comments || []).filter(c => c && !c.includes('| reason :') && !c.startsWith('(Due date change reason:)'));

            userComments.forEach(commentText => {
                if (!uniqueComments.has(commentText)) {
                    uniqueComments.set(commentText, { text: commentText, timestamp: ts });
                }
            });
        });

        const finalComments = Array.from(uniqueComments.values());

        finalComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        return finalComments.map((c, i) => ({ ...c, id: `comment-${i}-${c.timestamp}` }));
    }, [taskHistory, isLoadingHistory]);

    const allRemarksAndLogs = React.useMemo(() => {
        if (isLoadingHistory) return [];

        const historyOldestFirst = [...taskHistory].sort((a, b) => new Date(a.createdAt || a.updatedAt) - new Date(b.createdAt || b.updatedAt));
        
        const uniqueLogs = new Map();
        const uniqueRemarks = new Map();

        historyOldestFirst.forEach(h => {
            const ts = h.updatedAt || h.createdAt;

            (h.comments || [])
                .filter(c => c && c.includes('| reason :'))
                .forEach(logText => {
                    if (!uniqueLogs.has(logText)) {
                        uniqueLogs.set(logText, { text: logText, timestamp: ts });
                    }
                });
            
            (h.remarks || [])
                .filter(r => r)
                .forEach(remarkText => {
                    if (!uniqueRemarks.has(remarkText)) {
                        uniqueRemarks.set(remarkText, { text: remarkText, timestamp: ts });
                    }
                });
        });

        const allItems = [
            ...Array.from(uniqueLogs.values()),
            ...Array.from(uniqueRemarks.values())
        ];

        allItems.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        return allItems.map((item, index) => {
            if (item.text.includes('| reason :')) {
                return <SystemLogItem key={`item-log-${index}-${item.timestamp}`} logText={item.text} timestamp={item.timestamp} />;
            } else {
                return <RemarkItem key={`item-remark-${index}-${item.timestamp}`} remarkText={item.text} timestamp={item.timestamp} />;
            }
        });
    }, [taskHistory, isLoadingHistory]);
    
    useEffect(() => {
        if (successMessage || errorMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
                setErrorMessage('');
            }, MESSAGE_TIMEOUT_DURATION);
            return () => clearTimeout(timer);
        }
    }, [successMessage, errorMessage]);

    if (isLoadingTask || !loggedInUser) return <div className="task-detail-page"><p className="loading-text">Loading task...</p></div>;
    if (!task) return (<div className="task-detail-page"><div className="page-header"><button onClick={() => navigate('/tasks')} className="back-link-button"><IoArrowBack /> Back</button></div><div className="error-message full-page-error">Task data not found.</div></div>);
    
    const isReportingManager = loggedInUser?.role?.name === 'REPORTING MANAGER';
    const formattedCategories = availableCategories.map(cat => ({ id: cat, displayName: cat, name: cat }));
    const formattedPriorities = availablePriorities.map(p => ({ id: p, displayName: p, name: p }));
    const formattedAssignees = visibleAssignees.map(u => ({ id: String(u.id), displayName: `${u.name}`, name: u.name }));
    const filteredStatusesForEdit = availableStatuses.filter(status => { if ((status || '').toLowerCase() === 'completed') return canCompleteTask; return true; }).map(s => ({ id: s.toLowerCase().replace(/_/g, ' '), displayName: s.replace(/\b\w/g, l => l.toUpperCase()), name: s.replace(/\b\w/g, l => l.toUpperCase()) }));
    const formattedProjects = availableProjects.map(p => ({ ...p, displayName: p.name }));
    const formattedCustomers = availableCustomers.map(c => ({ ...c, displayName: c.name }));

    if (isEmailScreenOpen) {
        return (
            <div className="task-detail-page email-screen-view">
                <div className="page-header">
                    <button onClick={handleCloseEmailScreen} className="back-link-button"><IoArrowBack /> Back to Task Details</button>
                    <div className="header-actions">
                        <button onClick={handleSendEmail} className="button-submit" disabled={isGeneratingPdf || isSendingEmail}>
                            {isGeneratingPdf ? 'Processing...' : isSendingEmail ? 'Sending...' : 'Send Email'}
                        </button>
                    </div>
                </div>
                
                {successMessage && <div className={`inline-message ${isSendingEmail || isGeneratingPdf ? 'info-message' : 'success-message'}`}>{successMessage}</div>}
                {errorMessage && <div className="inline-message error-message">{errorMessage}</div>}

                <div className="email-screen-content">
                    <div className="email-form-panel">
                        <h2>Send Task Details for "{task.title}" #{task.taskId}</h2>
                        {renderRecipientInput('to')}
                        {renderRecipientInput('cc')}
                        {renderRecipientInput('bcc')}
                        {emailError.general && <div className="error-message">{emailError.general}</div>}
                    </div>
                    <div className="email-attachments-panel">
                        <h3>Attachments</h3>
                        <div className="email-attachments-list">
                            <p className="summary-notice">A Task Summary PDF will be automatically generated and attached.</p>
                            {attachments.map(att => (
                                <div key={att.id} className="attachment-checkbox-item">
                                    <input 
                                        type="checkbox" 
                                        id={`att-${att.id}`} 
                                        checked={emailFormData.selectedAttachmentIds.includes(att.id)} 
                                        onChange={() => { 
                                            const newSelection = emailFormData.selectedAttachmentIds.includes(att.id) 
                                                ? emailFormData.selectedAttachmentIds.filter(id => id !== att.id) 
                                                : [...emailFormData.selectedAttachmentIds, att.id];
                                            setEmailFormData(prev => ({ ...prev, selectedAttachmentIds: newSelection })); 
                                        }} 
                                    />
                                    <label htmlFor={`att-${att.id}`}>{decodeURIComponent(att.file_url.split('/').pop())}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isEditMode) {
        return (
            <div className="task-detail-page">
                <div className="page-header"><button onClick={handleExitEditMode} className="back-link-button"><IoArrowBack /> Back to Task Details</button></div>
                {errorMessage && <div className="inline-message error-message">{errorMessage}</div>}
                <form onSubmit={handleEditSubmit} className="edit-task-form" noValidate>
                    <div className="form-main-panel">
                        <div className="form-section">
                            <h2>Task Information</h2>
                            <div className="form-group"><label>Category</label><SearchableDropdown options={formattedCategories} selectedValues={editFormData.category} onChange={(v) => handleEditFormChange({ target: { name: 'category', value: v } })} placeholder="Select Category" disabled={!isSuperAdmin}/></div>
                            <div className="form-group"><label>Title</label><input type="text" name="title" value={editFormData.title} onChange={handleEditFormChange} required className="form-input" disabled={!isSuperAdmin}/></div>
                            <div className="form-group"><label>Description</label><textarea name="description" rows="4" value={editFormData.description} onChange={handleEditFormChange} className="form-input" disabled={!isSuperAdmin}/></div>
                            <div className="form-group"><label>Customer (Optional)</label><SearchableDropdown options={formattedCustomers} selectedValues={editFormData.customerId} onChange={(v) => handleEditFormChange({ target: { name: 'customerId', value: v } })} placeholder="Select Customer" allowClear={true} disabled={!isSuperAdmin}/></div>
                            <div className="form-group"><label>Project (Optional)</label><SearchableDropdown options={formattedProjects} selectedValues={editFormData.projectId} onChange={(v) => handleEditFormChange({ target: { name: 'projectId', value: v } })} placeholder="Select Project" allowClear={true} disabled={!isSuperAdmin}/></div>
                            <div className="form-group"><label>Assets (Optional)</label><SearchableDropdown options={availableAssets} selectedValues={editFormData.assetIds || []} onChange={(v) => handleEditFormChange({ target: { name: 'assetIds', value: v } })} placeholder="Select Assets" isMultiSelect={true} /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Status</label><SearchableDropdown options={filteredStatusesForEdit} selectedValues={editFormData.status} onChange={(v) => handleEditFormChange({ target: { name: 'status', value: v } })} placeholder="Select Status" /></div>
                                <div className="form-group"><label>Priority</label><SearchableDropdown options={formattedPriorities} selectedValues={editFormData.priority} onChange={(v) => handleEditFormChange({ target: { name: 'priority', value: v } })} placeholder="Select Priority" /></div>
                            </div>
                            <div className="form-group"><label>Add Comment (optional)</label><textarea name="newComment" rows="3" value={editFormData.newComment} onChange={handleEditFormChange} placeholder="Add a comment to log with this update..." className="form-input" /></div>
                        </div>
                    </div>
                    <div className="form-side-panel">
                        <div className="form-section">
                            <h2>Assignment</h2>
                            <div className="form-group"><label>Assignee</label><SearchableDropdown options={formattedAssignees} selectedValues={editFormData.assigneeId} onChange={(v) => handleEditFormChange({ target: { name: 'assigneeId', value: v } })} placeholder="Select Assignee" disabled={!isSuperAdmin && !isReportingManager} /></div>
                        </div>
                        <div className="form-section">
                            <h2>Due Date</h2>
                            <div className="form-group">
                                <label>Due Date & Time</label>
                                <div className="duedate-inputs">
                                    <div className="date-picker-dmy">
                                        <select name="dueDay" value={editFormData.dueDay} onChange={handleEditFormChange}><option value="">Day</option>{Array.from({ length: 31 }, (_, i) => String(i + 1)).map(d => <option key={`day-${d}`} value={d}>{d}</option>)}</select>
                                        <select name="dueMonth" value={editFormData.dueMonth} onChange={handleEditFormChange}><option value="">Month</option>{allMonths.map(m => <option key={`month-${m.value}`} value={m.name}>{m.name}</option>)}</select>
                                        <select name="dueYear" value={editFormData.dueYear} onChange={handleEditFormChange}><option value="">Year</option>{Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(y => <option key={`year-${y}`} value={y}>{y}</option>)}</select>
                                    </div>
                                    <div className="time-picker-24h">
                                        <select name="hour" value={editFormData.hour} onChange={handleEditFormChange}><option value="">HH</option>{allHours.map(h => <option key={`hour-${h}`} value={h}>{h}</option>)}</select>
                                        <span>:</span>
                                        <select name="minute" value={editFormData.minute} onChange={handleEditFormChange}><option value="">MM</option>{allMinutes.map(m => <option key={`min-${m}`} value={m}>{m}</option>)}</select>
                                    </div>
                                </div>
                            </div>
                            {isDueDateDirty && (<div className="form-group"><label>Reason for Due Date Change <span className="required-asterisk">*</span></label><textarea value={dueDateChangeReason} onChange={(e) => { setDueDateChangeReason(e.target.value); if (dueDateReasonError) setDueDateReasonError(''); }} className={`form-input ${dueDateReasonError ? 'invalid-field' : ''}`} rows="2" placeholder="Please provide a reason..." required />{dueDateReasonError && <span className="error-text">{dueDateReasonError}</span>}</div>)}
                        </div>
                    </div>
                    <div className="form-actions"><button type="submit" className="button-submit" disabled={isSubmittingEdit}>{isSubmittingEdit ? 'Saving...' : 'Save Changes'}</button></div>
                </form>
            </div>
        );
    }
    
    return (
        <div className="task-detail-page">
            <div className="page-header">
                <button onClick={handleGoBack} className="back-link-button"><IoArrowBack /> Back to Tasks</button>
                <div className="header-actions">
                    {isTaskCompleted && (<button onClick={handleOpenEmailScreen} className="action-btn email-btn" disabled={isSendingEmail || isGeneratingPdf} title="Send Task Details via Email"><IoMailOutline /> Send Email</button>)}
                    <button className="edit-task-btn" onClick={handleEnterEditMode} disabled={!canEditTask || isLoadingTask}><IoPencil /> Edit Task</button>
                </div>
            </div>

            {successMessage && <div className={`inline-message ${isSendingEmail || isGeneratingPdf ? 'info-message' : 'success-message'}`}>{successMessage}</div>}
            {errorMessage && <div className="inline-message error-message">{errorMessage}</div>}

            <div className="task-header-section"><h1>{task.title} <span className="task-id-display">#{taskId}</span></h1><div className="task-meta-tags">{task.status && <span className={`task-badge status-${(task.status || '').toLowerCase().replace(/_/g, '-')}`}>{task.status.replace(/_/g, ' ')}</span>}{task.priority && <span className={`task-badge priority-${(task.priority || '').toLowerCase()}`}>{task.priority}</span>}</div></div>
            <div className="task-content-layout">
                <main className="task-main-content">
                    <div className="task-tabs"><button className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}><IoReorderFourOutline /> Details</button><button className={`tab-btn ${activeTab === 'comments' ? 'active' : ''}`} onClick={() => setActiveTab('comments')}><IoChatbubblesOutline /> Comments</button><button className={`tab-btn ${activeTab === 'remarks' ? 'active' : ''}`} onClick={() => setActiveTab('remarks')}><IoMegaphoneOutline /> Remarks & Activity</button><button className={`tab-btn ${activeTab === 'attachments' ? 'active' : ''}`} onClick={() => setActiveTab('attachments')}><IoAttach /> Attachments</button></div>
                    <div className="tab-content">
                        {activeTab === 'details' && (<section><h2>Description</h2><p className="task-description-text">{task.description || <span className="no-content-text">No description provided.</span>}</p></section>)}
                        {activeTab === 'comments' && (<section><h2>Comments</h2>{isLoadingHistory ? (<p>Loading...</p>) : processedComments.length > 0 ? (<div className="comments-list">{processedComments.map(c => (<CommentItem key={c.id} commentText={c.text} timestamp={c.timestamp} />))}</div>) : <p className="no-content-text">No comments yet.</p>}</section>)}
                        {activeTab === 'remarks' && (<section><h2>Remarks & Activity Log</h2>{isLoadingHistory ? (<p>Loading...</p>) : allRemarksAndLogs.length > 0 ? (<div className="remarks-list">{allRemarksAndLogs}</div>) : <p className="no-content-text">No remarks or activity yet.</p>}</section>)}
                        {activeTab === 'attachments' && (
                            <section>
                                <h2>Attachments</h2>
                                {isLoadingAttachments ? (<p>Loading...</p>) : attachments.length > 0 ? (<div className="attachments-list">{attachments.map((att) => <AttachmentItem key={att.id} attachment={att} onDelete={handleDeleteAttachment} canDelete={true} />)}</div>) : (<p className="no-content-text">No attachments found.</p>)}
                                <div className="upload-section-container">
                                    <h3>Upload New Attachment</h3>
                                    <label 
                                        htmlFor="page-file-input"
                                        className={`attachment-upload-area ${!canUploadAttachment ? 'disabled' : ''} ${isDragging ? 'drag-over' : ''}`}
                                        onDrop={handleFileDrop}
                                        onDragOver={handleDragOver}
                                        onDragEnter={handleDragEnter}
                                        onDragLeave={handleDragLeave}
                                    >
                                        {canUploadAttachment ? (
                                            <>
                                                <div className="file-upload-content">
                                                    <IoCloudUploadOutline />
                                                    <span>{isDragging ? 'Drop files to upload' : 'Drag & Drop files here or Click to Upload'}</span>
                                                </div>
                                                <input id="page-file-input" type="file" onChange={handleFileChange} disabled={isUploading} className="file-upload-hidden" multiple/>
                                                {isUploading && <p className="loading-text small">Uploading...</p>}
                                            </>
                                        ) : (
                                            <p className="no-content-text">Uploading is disabled for completed tasks.</p>
                                        )}
                                    </label>
                                </div>
                            </section>
                        )}
                    </div>
                </main>
                <aside className="task-sidebar-details">
                    <div className="details-card">
                        <h3>Details</h3>
                        <ul>
                            <li><IoCalendarOutline /><div><span>Created On</span><strong>{formatDateForDisplay(task.createdAt, true)}</strong></div></li>
                            {task.updatedAt && new Date(task.updatedAt) > new Date(task.createdAt) && (<li><IoCalendarOutline /><div><span>Last Updated</span><strong>{formatDateForDisplay(task.updatedAt, true)}</strong></div></li>)}
                            <li><IoCalendarOutline /><div><span>Due Date</span><strong>{formatDateForDisplay(task.dueDate, true)}</strong></div></li>
                            {!isTaskCompleted && remainingTime && (<li className={remainingTime.includes('Overdue') ? 'detail-overdue' : ''}><IoHourglassOutline /><div><span>Time Remaining</span><strong>{remainingTime}</strong></div></li>)}
                            {task.reminderDays > 0 && <li><IoNotificationsOutline /><div><span>Reminder</span><strong>{task.reminderDays} day(s) before due</strong></div></li>}
                            <li><IoPersonOutline /><div><span>Created By</span><strong>{task.createdBy || 'N/A'}</strong></div></li>
                            <li><IoPersonOutline /><div><span>Assigned To</span><strong>{task.assigneeName || 'N/A'}</strong></div></li>
                            {task.category && <li><IoPricetagOutline /><div><span>Category</span><strong>{typeof task.category === 'object' ? task.category.name : task.category}</strong></div></li>}
                            {(task.projectName || task.project) && <li><IoPricetagOutline /><div><span>Project</span><strong>{(typeof task.project === 'object' ? task.project.projectName : task.project) || task.projectName}</strong></div></li>}
                            {task.assetName && task.assetName.length > 0 && (<li><IoHardwareChipOutline /><div><span>Assets</span><div className="asset-list-sidebar">{task.assetName.map((asset, index) => (<strong key={index}>{asset}</strong>))}</div></div></li>)}
                        </ul>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default TaskDetailPage;